// VOICE-1 (docs/specs/voice-1.md): the signed-in user's own voice print.
//
// Every request is gated on `useFeatureEnabled('voice_prints')` by the callers: with the
// feature off, no voice-print request fires and no affordance renders. The embedding never
// reaches the browser — these only carry status.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EnrollVoicePrintRequest, VoicePrint } from '@scribe/core/types';
import { useApi } from './ApiContext';
import { isMock } from './mock';

const KEY = ['voice-print'];
// Mock mode mirrors the server's consent version so the UI flow is exercisable offline.
const MOCK_CONSENT_VERSION = '2026-10-16';
let mockPrint: VoicePrint = { status: 'none', consent_version: MOCK_CONSENT_VERSION };

export function useMyVoicePrint(enabled: boolean) {
  const { client } = useApi();
  return useQuery<VoicePrint>({
    queryKey: KEY,
    queryFn: () => (isMock() ? Promise.resolve(mockPrint) : client.getMyVoicePrint()),
    enabled,
    // A new print is built by a background sweep within a few minutes.
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 15_000 : false),
  });
}

export function useEnrollVoicePrint(recordingId: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Omit<EnrollVoicePrintRequest, 'recording_id'>) => {
      if (isMock()) {
        mockPrint = {
          status: 'pending',
          consent_version: MOCK_CONSENT_VERSION,
          source_recording_id: recordingId,
          source_label: body.speaker_label,
        };
        return mockPrint;
      }
      return client.enrollMyVoicePrint({ ...body, recording_id: recordingId });
    },
    onSuccess: (print) => {
      qc.setQueryData(KEY, print);
      // The server also names the speaker "you" in this recording.
      void qc.invalidateQueries({ queryKey: ['transcript', recordingId] });
    },
  });
}

export function useDeleteVoicePrint() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (isMock()) {
        mockPrint = { status: 'none', consent_version: MOCK_CONSENT_VERSION };
        return;
      }
      await client.deleteMyVoicePrint();
    },
    onSuccess: () => {
      qc.setQueryData<VoicePrint | undefined>(KEY, (old) =>
        old ? { status: 'none', consent_version: old.consent_version } : old,
      );
    },
  });
}
